import React, { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Sparkles, Loader2, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { ACTIVITY_TYPE_LABELS } from '../../lib/constants';

const AUD_KEY = { Kinderen: 'age.children', Jongeren: 'age.youth', Volwassenen: 'age.adults', Senioren: 'age.seniors' };
const DAY_KEY = { ma: 'weekday.monday', di: 'weekday.tuesday', wo: 'weekday.wednesday', do: 'weekday.thursday', vr: 'weekday.friday', za: 'weekday.saturday', zo: 'weekday.sunday' };

// Bouwt een leesbare omschrijving van de begrepen filters in de actieve taal.
const describe = (f, t) => {
  if (!f) return '';
  const parts = [];
  if (f.q) parts.push(`"${f.q}"`);
  if (f.activity_type && ACTIVITY_TYPE_LABELS[f.activity_type]) parts.push(ACTIVITY_TYPE_LABELS[f.activity_type].label.toLowerCase());
  if (f.target_audience && AUD_KEY[f.target_audience]) parts.push(`${t('ai.for')} ${t(AUD_KEY[f.target_audience]).toLowerCase()}`);
  if (f.day && DAY_KEY[f.day]) parts.push(`${t('ai.on')} ${t(DAY_KEY[f.day]).toLowerCase()}`);
  if (f.part_of_day) parts.push(t(`ai.part.${f.part_of_day}`));
  if (f.wijk) parts.push(`${t('ai.in')} ${f.wijk}`);
  return parts.join(' ');
};

export const AiSearchBar = () => {
  const { t, language } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(null); // { count, resolved, original, broadened }

  // We laten de getypte tekst bewust staan na het zoeken (ook als de AI de
  // zoekterm heeft verbreed). De input wordt alleen bij mount uit de URL gevuld
  // (gedeelde links) via de useState-initializer hierboven.

  const pushFilters = useCallback((f) => {
    const p = new URLSearchParams();
    if (f.q) p.set('q', f.q);
    if (f.activity_type) p.set('activity_type', f.activity_type);
    if (f.wijk) p.set('wijk', f.wijk);
    if (f.target_audience) p.set('target_audience', f.target_audience);
    if (f.day) p.set('day', f.day);
    if (f.part_of_day) p.set('part_of_day', f.part_of_day);
    if (f.date_from) p.set('date_from', f.date_from);
    if (f.date_to) p.set('date_to', f.date_to);
    if (f.is_holiday) p.set('is_holiday', 'true');
    if (f.season) p.set('season', f.season);
    if (f.period) p.set('period', f.period);
    navigate(`/?${p.toString()}`);
  }, [navigate]);

  const runSearch = async () => {
    const text = query.trim();
    if (text.length < 2 || loading) return;
    setLoading(true);
    try {
      const r = await api.post('/assistant/search', { query: text, language });
      const resolved = r.data.filters || { q: text };
      setMeta({
        count: r.data.result_count || 0,
        resolved,
        original: r.data.original_filters || resolved,
        broadened: r.data.broadened || [],
        periodLabel: r.data.period_label || '',
        notice: r.data.notice || '',
      });
      pushFilters(resolved);
    } catch {
      setMeta(null);
      pushFilters({ q: text });
    } finally {
      setLoading(false);
    }
  };

  const clear = () => { setQuery(''); setMeta(null); navigate('/'); };

  let answer = null;
  if (loading) {
    answer = t('ai.searching');
  } else if (meta) {
    const desc = describe(meta.resolved, t);
    const plural = meta.count === 1 ? t('ai.activity') : t('ai.activities');
    const periodPart = meta.periodLabel ? ` ${meta.periodLabel}` : '';
    if (meta.count === 0) {
      answer = `${t('ai.none_prefix')} ${describe(meta.original, t) || desc}${periodPart}. ${t('ai.none_suffix')}`;
    } else if (meta.broadened && meta.broadened.length) {
      answer = `${t('ai.broadened_prefix')} ${describe(meta.original, t)} ${t('ai.broadened_mid')} ${meta.count} ${plural} ${desc}${periodPart}:`;
    } else {
      answer = `${t('ai.found_prefix')} ${meta.count} ${plural}${desc ? ' ' + desc : ''}${periodPart}.`;
    }
    if (meta.notice) answer += ` ${meta.notice}`;
  }

  const rtl = language === 'ar';
  return (
    <div data-testid="ai-search" dir={rtl ? 'rtl' : 'ltr'}>
      <div className="relative">
        <Sparkles className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-hoi-coral pointer-events-none" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
          placeholder={t('ai.placeholder')}
          data-testid="ai-search-input"
          aria-label={t('ai.placeholder')}
          className="w-full h-14 pl-11 pr-32 rounded-2xl border-2 border-gray-200 focus:border-hoi-coral focus:outline-none text-base bg-white shadow-sm transition-colors"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {query && !loading && (
            <button onClick={clear} data-testid="ai-search-clear" aria-label="Wissen" className="text-gray-400 hover:text-gray-600 p-1">
              <X className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={runSearch}
            disabled={loading || query.trim().length < 2}
            data-testid="ai-search-btn"
            className="bg-hoi-coral hover:bg-hoi-coral/90 text-white rounded-xl px-4 h-10 text-sm font-semibold flex items-center gap-1.5 disabled:opacity-40 transition-colors"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            <span className="hidden sm:inline">{t('ai.ask')}</span>
          </button>
        </div>
      </div>
      <div
        data-testid="ai-search-answer"
        className="mt-2 flex items-start gap-2 rounded-xl bg-hoi-coral/5 border border-hoi-coral/15 px-3.5 py-2.5 text-sm text-primary min-h-[42px]"
      >
        <Sparkles className="w-4 h-4 text-hoi-coral mt-0.5 flex-shrink-0" aria-hidden="true" />
        <span className="leading-snug">{answer || t('ai.hint')}</span>
      </div>
    </div>
  );
};

export default AiSearchBar;
